import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ScrollText, Sparkles } from "lucide-react";

import { brand } from "../../theme/theme";
import { Button } from "../Button";
import { useAuth } from "../contexts/AuthContext";
import { getData } from "../hooks/getData";
import { settingsPath } from "../../Pages/Settings/settingsPaths";
import { AgencyPromptsModal } from "./AgencyPromptsModal";
import { aiConfigQueryKey, fetchAiConfig, updateAiConfig } from "../../services/contentGenerationService";
import { PROMPT_STEPS, resolveAgencyPrompts } from "./agencyPrompts";

const READER_ROLES = ["agency_admin", "consultant", "superadmin"];

const agencyIdOf = (client) => client?.agency_id ?? client?.agency?.id ?? client?.agency ?? null;

/**
 * "Configurar prompts" button + modal with the agency prompts that a generation step uses.
 * Hidden for roles without access; a superadmin needs `agencyId` or `clientId`.
 * The agency admin and the superadmin edit them in place; the consultant only reads.
 */
export function AgencyPromptsButton({ steps, platforms, agencyId, clientId, label = "Configurar prompts", hint, variant = "inline", sx }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, interceptorReady } = useAuth();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  const isSuperAdmin = role === "superadmin";
  const canRead = READER_ROLES.includes(role);

  // The superadmin has no agency of their own: resolve it from the client when not given.
  const needsClient = isSuperAdmin && !agencyId && Boolean(clientId);
  const { data: client } = getData(
    needsClient ? `clients/${clientId}/` : null,
    Boolean(interceptorReady && needsClient),
    0
  );
  const targetAgencyId = isSuperAdmin ? agencyId || agencyIdOf(client) : null;
  const enabled = Boolean(interceptorReady && canRead && (!isSuperAdmin || targetAgencyId));
  // Mirrors the backend: only the agency admin and the superadmin may PATCH the config.
  const canEdit = role === "agency_admin" || (isSuperAdmin && Boolean(targetAgencyId));

  const { data: config, isLoading, isError, refetch } = useQuery({
    queryKey: aiConfigQueryKey(targetAgencyId),
    queryFn: () => fetchAiConfig(targetAgencyId),
    enabled,
  });

  const prompts = useMemo(
    () => (config ? resolveAgencyPrompts(config, steps, platforms, isSuperAdmin) : []),
    [config, steps, platforms, isSuperAdmin]
  );

  if (!enabled || (config && !prompts.length)) return null;

  const handleSave = async (promptKey, text) => {
    const data = await updateAiConfig(targetAgencyId, { [promptKey]: text });
    queryClient.setQueryData(aiConfigQueryKey(targetAgencyId), data);
  };

  const agencyLabel = config?.agency_name ? `${config.agency_name}: ` : "";

  // Only the agency admin has the content prompts in their settings.
  const handleOpenSettings =
    role === "agency_admin"
      ? () => {
          setOpen(false);
          navigate(`${settingsPath("prompts")}?prompt=${encodeURIComponent(prompts[0]?.settingsKey || "")}`);
        }
      : undefined;

  return (
    <>
      {hint ? (
        <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", lineHeight: 1.5, ...sx }}>
          {hint} ·{" "}
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            sx={{
              p: 0,
              border: 0,
              background: "none",
              font: "inherit",
              fontWeight: 600,
              color: "#0F172A",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              cursor: "pointer",
              "&:hover": { color: brand.pink },
              "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2, borderRadius: "4px" },
            }}
          >
            {label}
          </Box>
        </Typography>
      ) : variant === "header" ? (
        <Button variant="white" onClick={() => setOpen(true)} aria-haspopup="dialog" sx={sx}>
          {isLoading ? <CircularProgress size={14} /> : <Sparkles size={16} />}
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {label}
          </Box>
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          sx={{ flexShrink: 0, px: 1, gap: 0.75, fontWeight: 600, ...sx }}
        >
          {isLoading ? <CircularProgress size={12} /> : <ScrollText size={14} color={brand.pink} />}
          {label}
          <ChevronRight size={14} />
        </Button>
      )}
      <AgencyPromptsModal
        open={open}
        onClose={() => setOpen(false)}
        prompts={prompts}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        canEdit={canEdit}
        onSave={handleSave}
        onOpenSettings={handleOpenSettings}
        description={
          canEdit
            ? `${agencyLabel}estas instrucciones se combinan con el briefing y la ficha del cliente. Los cambios que guardes se aplican a todos los clientes de la agencia.`
            : `${agencyLabel}estas instrucciones se combinan con tu briefing y la ficha del cliente. Solo lectura: solo un administrador de la agencia puede cambiarlas.`
        }
      />
    </>
  );
}

AgencyPromptsButton.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.oneOf(PROMPT_STEPS)).isRequired,
  platforms: PropTypes.arrayOf(PropTypes.string),
  agencyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  // Renders a line of text ending in a "label" link instead of a button.
  hint: PropTypes.string,
  // "header": page-header button (white, settings icon) instead of the inline link.
  variant: PropTypes.oneOf(["inline", "header"]),
  sx: PropTypes.object,
};
